import * as React from "react";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import LastPageIcon from "@mui/icons-material/LastPage";
import TextField from "@mui/material/TextField";
import { useTheme } from "@mui/material/styles";
import { isNumeric } from "../../utils";

export default function TablePaginationActions(props) {
  const theme = useTheme();
  const { count, page, rowsPerPage, onPageChange } = props;
  const [gotoPage, setGotoPage] = React.useState(page + 1);
  const [upperPageLimit, setUpperPageLimit] = React.useState(0);

  React.useEffect(() => {
    setUpperPageLimit(Math.ceil(count / rowsPerPage));
  }, [count, rowsPerPage]);

  React.useEffect(() => {
    setGotoPage(page + 1);
  }, [page]);

  const handleFirstPageButtonClick = (event) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (event) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (event) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  const handleInputChange = React.useCallback(
    (event) => {
      const pageNo = event?.target?.value;
      if (!isNumeric(pageNo) && pageNo.length !== 0) {
        return event.preventDefault();
      }
      const currentPageNo = parseInt(pageNo, 10);
      const isInvalidRange = currentPageNo > upperPageLimit;
      if (
        (!isInvalidRange && currentPageNo > 0 && currentPageNo !== 0) ||
        !pageNo.trim().length
      ) {
        return setGotoPage(!!pageNo.trim().length ? currentPageNo : "");
      }
      return false;
    },
    [upperPageLimit]
  );

  const handleInputKeyPress = React.useCallback(
    (event) => {
      if (event.key === "Enter" && !!gotoPage) {
        return onPageChange(event, gotoPage >= 1 ? gotoPage - 1 : 1);
      }
      return false;
    },
    [gotoPage, onPageChange]
  );

  const handleInputBlur = React.useCallback(
    (event) => {
      if (gotoPage) {
        return onPageChange(event, gotoPage - 1);
      }
    },
    [gotoPage, onPageChange]
  );

  return (
    <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0, ml: 2.5 }}>
      <IconButton
        data-testid="first-button"
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label="first page"
      >
        {theme.direction === "rtl" ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>
      <IconButton
        data-testid="previous-button"
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label="previous page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowRight />
        ) : (
          <KeyboardArrowLeft />
        )}
      </IconButton>
      <TextField
        id="goto-page-field"
        data-testid="page-input"
        variant="outlined"
        size="small"
        style={{ width: 60 }}
        value={gotoPage}
        onChange={handleInputChange}
        onKeyPress={handleInputKeyPress}
        onBlur={handleInputBlur}
        autoComplete="off"
      />
      <IconButton
        data-testid="next-button"
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowLeft />
        ) : (
          <KeyboardArrowRight />
        )}
      </IconButton>
      <IconButton
        data-testid="last-button"
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        {theme.direction === "rtl" ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </Box>
  );
}
